import React from 'react';

import { faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';

import { CURRENCIES } from 'src/features/payStations/types';
import DetailsSection from 'src/features/reservations/components/DetailsSection';
import ReservationMetrics from 'src/features/reservations/components/ReservationMetrics';
import { useReservationTypeByVersionId } from 'src/features/reservations/hooks/useReservationTypeByVersionId';
import { reservationMessages } from 'src/features/reservations/utils/messages';
import { GetPriceTaxAmounts, LatestScheduleChangesBadge } from 'src/features/reservations/utils/utils';
import { Button } from 'src/registry/ui/Button';
import { useTitleStore } from 'src/stores/useTitleStore';
import { getFormattedDateTime } from 'src/utils/getFormattedDateTime';
import { generalMessages, generalPriceMessages } from 'src/utils/messages';

const ReservationTypeDetails = () => {
    const { reservationType: dynamicReservationType, reservationTypeId } = useParams();
    const { parkingId } = useParams();
    const { formatMessage } = useIntl();
    const [reservationId, setReservationId] = React.useState<string>('');
    const { setTitle, removeTitle } = useTitleStore();

    const { data: reservationType } = useReservationTypeByVersionId({
        parkingId,
        versionId: reservationTypeId,
    });

    React.useEffect(() => {
        if (!reservationType) return;
        setTitle(reservationType.name);
        return removeTitle;
    }, [reservationType, formatMessage]);

    React.useEffect(() => {
        if (reservationType) setReservationId(reservationType?.id);
    }, [reservationTypeId, reservationType?.id]);

    const { data: reservationTypeDetails } = useReservationTypeByVersionId({
        parkingId,
        versionId: reservationId,
    });

    const getEntryExitToleranceMin = (field: 'entry_tolerance_minutes' | 'exit_tolerance_minutes') => {
        if (!reservationType) return '-';
        const fieldValue = reservationTypeDetails && reservationTypeDetails[field];
        return `${formatMessage(reservationMessages.minutes, { value: fieldValue })}`;
    };

    const reservationValidFrom = `${getFormattedDateTime(reservationTypeDetails?.valid_from).formattedDate} ${getFormattedDateTime(reservationTypeDetails?.valid_from).formattedTime}`;
    const reservationValidTo =
        reservationTypeDetails?.valid_to &&
        `${getFormattedDateTime(reservationTypeDetails?.valid_to).formattedDate} ${getFormattedDateTime(reservationTypeDetails?.valid_to).formattedTime}`;

    const currency: CURRENCIES | undefined = reservationTypeDetails?.currency;

    return (
        <>
            <Button asChild className="-mt-6 place-self-start p-0" variant="link">
                <Link
                    to={`/parking-management/${parkingId}/reservations/${
                        dynamicReservationType ? `dynamic/${dynamicReservationType}/versions` : 'fixed'
                    }`}
                >
                    <FontAwesomeIcon icon={faChevronLeft} className="opacity-50" />
                    {formatMessage(
                        dynamicReservationType
                            ? reservationMessages.backVersions
                            : reservationMessages.backReservations,
                    )}
                </Link>
            </Button>
            <ReservationMetrics selectedReservationType={reservationTypeDetails} disableSwitch />
            <div className="flex flex-col gap-y-4 md:grid md:grid-cols-1 md:gap-6 lg:grid-cols-2 lg:gap-6">
                <DetailsSection
                    title={formatMessage(reservationMessages.currentVersion)}
                    titleBadge={<LatestScheduleChangesBadge reservationTypeDetails={reservationTypeDetails} />}
                    data={[
                        {
                            title: formatMessage(reservationMessages.name),
                            value: reservationTypeDetails?.name,
                        },
                        {
                            title: formatMessage(reservationMessages.description),
                            value: reservationTypeDetails?.description,
                        },
                        {
                            title: formatMessage(reservationMessages.isActive),
                            value: reservationTypeDetails?.enabled
                                ? formatMessage(generalMessages.yes)
                                : formatMessage(generalMessages.no),
                        },
                        {
                            title: formatMessage(reservationMessages.reservationValidFrom),
                            value: reservationValidFrom,
                        },
                        {
                            title: formatMessage(reservationMessages.reservationValidTo),
                            value: reservationValidTo,
                        },
                    ]}
                />
                <DetailsSection
                    title={formatMessage(generalPriceMessages.pricing)}
                    data={[
                        {
                            title: formatMessage(generalPriceMessages.price),
                            value: GetPriceTaxAmounts(reservationTypeDetails?.price, currency),
                        },
                        {
                            title: `${formatMessage(generalPriceMessages.vat)} %`,
                            value:
                                reservationTypeDetails?.tax_percent && reservationTypeDetails?.price
                                    ? `${(parseFloat(reservationTypeDetails.tax_percent) * 100).toFixed(2)}%`
                                    : null,
                        },
                        {
                            title: formatMessage(generalPriceMessages.currency),
                            value: reservationTypeDetails?.price ? reservationTypeDetails?.currency : null,
                        },
                        {
                            title: formatMessage(generalPriceMessages.vatAmount),
                            value: GetPriceTaxAmounts(reservationTypeDetails?.tax_amount, currency),
                        },
                        {
                            title: formatMessage(generalPriceMessages.priceWithVat),
                            value: GetPriceTaxAmounts(reservationTypeDetails?.price_with_tax, currency),
                        },
                    ]}
                />
                <DetailsSection
                    title={formatMessage(reservationMessages.stayRules)}
                    data={[
                        {
                            title: formatMessage(reservationMessages.allowedSpots),
                            value: reservationTypeDetails?.allowed_spaces,
                        },
                        {
                            title: formatMessage(reservationMessages.entryTolerance),
                            value: getEntryExitToleranceMin('entry_tolerance_minutes'),
                        },
                        {
                            title: formatMessage(reservationMessages.exitTolerance),
                            value: getEntryExitToleranceMin('exit_tolerance_minutes'),
                        },
                        {
                            title: formatMessage(reservationMessages.reservedSpot),
                            value: reservationTypeDetails?.reserve_space
                                ? formatMessage(generalMessages.yes)
                                : formatMessage(generalMessages.no),
                        },
                    ]}
                />
            </div>
        </>
    );
};

export default ReservationTypeDetails;
